import { zip } from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BaseModalPageComponent } from '../../base-modal.component';
import {
  PeopleService,
  LanguageService,
  ApplicationService,
  MachineFamillyService,
  LocalEntityService
} from '../../shared/services/index';
import { ProfilPipe, LocalEntityPipe } from '../../pipes/index';
import { ModalComponent } from '../../modal/modal.component';
import * as _ from 'lodash';
import { Person } from '../../shared/models';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
    templateUrl: './new.component.html',
    standalone: false
})
export class PeopleNewPageComponent extends BaseModalPageComponent implements OnInit {
  public processing = false;
  public personApplicationTypes: any[] = [];
  public languages: any[];
  public applicationTypes: any[];
  public permissions: any;
  public productLines: any[];
  public localEntities: any;
  public currentApplicationType: number;
  public currentPerson: Person;
  newForm: UntypedFormGroup;
  @ViewChild('permissionsModal', { static: true }) permissionsModal: ModalComponent;
  @ViewChild('productLinesModal', { static: true }) productLinesModal: ModalComponent;
  @ViewChild('localEntitiesModal', { static: true }) localEntitiesModal: ModalComponent;
  @ViewChild('officeModal', { static: true }) officeModal: ModalComponent;

  constructor(
    private language: LanguageService,
    private activatedRoute: ActivatedRoute,
    private peopleService: PeopleService,
    private applicationService: ApplicationService,
    private router: Router,
    location: Location,
    private profilPipe: ProfilPipe,
    private localEntityPipe: LocalEntityPipe,
    private machineFamillyService: MachineFamillyService,
    private localEntityService: LocalEntityService,
    private permissionService: PermissionService
  ) {
    super(location);
  }

  ngOnInit(): void {
    const self = this;
    self.localEntityService
      .getLocalEntities()
      .pipe(zip(this.applicationService.getApplicationTypes()))
      .subscribe(([localEntities, types]) => {
        self.localEntities = _.orderBy(localEntities, ['label']);
        self.language.getLanguageList().subscribe((data) => (this.languages = data));
        self.applicationTypes = types;
        self.initRoute();
      });
  }

  initRoute(): void {
    this.initData();
  }

  initData(): void {
    this.permissionService
      .getPermissionsList()
      .pipe(
        zip(
          this.machineFamillyService.getProductLines(),
          this.peopleService.getCurrent(),
          this.applicationService.getCurrent()
        )
      )
      .subscribe(([permissions, productLines, currentPerson, currentApplicationType]) => {
        this.currentPerson = currentPerson;
        this.currentApplicationType = currentApplicationType;
        this.permissions = _.orderBy(permissions, ['label']);
        productLines.push(this.machineFamillyService.getAllProductLineItem());
        this.productLines = _.map(productLines, (p) => ({
          id: 'productline-' + p.id,
          name: p.name,
          label: p.name
        }));
        this.productLines = _.orderBy(this.productLines, ['name']);
        this.initForm();
      });
  }

  initForm() {
    this.newForm = new UntypedFormGroup({
      Name: new UntypedFormControl(''),
      Description: new UntypedFormControl(''),
      Phone: new UntypedFormControl(
        '',
        Validators.pattern(/^([+]?\d{1,2}[-| \s]?)?(\d{1,4}[-| ]?){1,4}?(\d{1,4}[-| ]?)\d{1,4}$/)
      ),
      UserName: new UntypedFormControl(''),
      Email: new UntypedFormControl(''),
      Language: new UntypedFormControl(''),
      permissions: new UntypedFormControl(this.processPermissions(null)),
      productLine: new UntypedFormControl(this.processProductLines(null)),
      localEntities: new UntypedFormControl(this.processLocalEntities(null)),
      office: new UntypedFormControl('')
    });
  }

  public openProductLinesSelector(): void {
    this.openMultipleSelector(this.productLinesModal, this.newForm.controls['productLine'].value);
  }

  public selectProductLinesMenu(item: any) {}

  public onProductLinesMenuClose(e: any) {
    this.newForm.controls['productLine'].markAsTouched();
  }

  isInPersonApplicationTypes(applicationType: number) {
    return this.personApplicationTypes && this.personApplicationTypes.indexOf(applicationType) !== -1;
  }

  updateApplicationTypes(applicationType: number) {
    if (!this.isInPersonApplicationTypes(applicationType)) {
      this.personApplicationTypes.push(applicationType);
    } else {
      const index = this.applicationTypes.indexOf(applicationType);
      this.personApplicationTypes.splice(index, 1);
    }
  }

  save(model: any): void {
    this.processing = true;
    model.applicationTypes = this.currentPerson.isHost ? this.personApplicationTypes : [this.currentApplicationType];
    _.remove(model.permissions, (t: any) => !t.selected);
    _.remove(model.localEntities, (t: any) => !t.selected);
    model.permissions = _.map(model.permissions, (t: any) => t.value);
    model.productLine = model.productLine.filter((p) => p.selected).map((p) => p.id);
    model.localEntities = _.map(model.localEntities, (t: any) => t.value);
    this.peopleService.addPerson(model).subscribe((data: any) => {
      this.processing = false;
      this.goToBack();
    });
  }

  processPermissions(permissions: any): any {
    const clonedPermissions = _.cloneDeep(this.permissions);
    _.each(permissions, (el) => {
      const i = _.findIndex(clonedPermissions, ['value', el]);
      if (i !== -1) {
        clonedPermissions[i].selected = true;
      }
    });
    return clonedPermissions;
  }

  processLocalEntities(localEntities: any): any {
    const clonedLocalEntities = _.cloneDeep(this.localEntities);
    _.each(localEntities, (el) => {
      const i = _.findIndex(clonedLocalEntities, ['value', el]);
      if (i !== -1) {
        clonedLocalEntities[i].selected = true;
      }
    });
    return clonedLocalEntities;
  }

  public getSelectedLocalEntitiesValues(): string {
    const localEntities = this.newForm.controls['localEntities'].value;
    const defaultValue = 'Select a local entity';
    let selectedValues = _.filter(localEntities, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.localEntityPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public getSelectedValues(): string {
    const permissions = this.newForm.controls['permissions'].value;
    const defaultValue = 'Select a permission';
    let selectedValues = _.filter(permissions, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.profilPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  processProductLines(productLines: any): string {
    const clonedProductLines = _.cloneDeep(this.productLines);
    _.each(productLines, (el) => {
      const i = _.findIndex(clonedProductLines, ['id', el]);
      if (i !== -1) {
        clonedProductLines[i].selected = true;
      }
    });
    return clonedProductLines;
  }

  public getSelectedProductLineValues(): string {
    const productLines = this.newForm.controls['productLine'].value;
    const defaultValue = 'Select a product line';
    let selectedValues = _.filter(productLines, (item: any) => item.selected);
    selectedValues = selectedValues.map((s) => s.name);
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public openPermissionsSelector(): void {
    this.openMultipleSelector(this.permissionsModal, this.newForm.controls['permissions'].value);
  }

  public openLocalEntitiesSelector(): void {
    this.openMultipleSelector(this.localEntitiesModal, this.newForm.controls['localEntities'].value);
  }

  public selectPermissionsMenu(item: any) {}
  public selectLocalEntitiesMenu(item: any) {}

  public selectOfficeMenu(item: any) {
    const selectedIndex = this.newForm.controls['office'].value === item.selectedIndex ? '' : item.selectedIndex;
    this.newForm.controls['office'].setValue(selectedIndex);
  }

  public openOfficeSelector(): void {
    this.openSelector(this.officeModal, this.localEntities, this.newForm.controls['office'].value);
  }

  cancel(): void {
    this.goToBack();
  }
}
